import '@livekit/components-styles'
import {
  LiveKitRoom,
  RoomAudioRenderer,
  useDisconnectButton,
  useLocalParticipant,
  useRemoteParticipants,
} from '@livekit/components-react'
import type { MediaSettings } from '@workspace/seedforge-ui/pitch'
import { Badge } from '@workspace/ui/components/badge'
import { ConfirmButton } from '@workspace/ui/components/confirm-button'
import { SessionTimer } from '@workspace/ui/components/session-timer'
import { Loader2, LogOut } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { PdfViewer } from '../../../components/pdf-viewer'
import { useFinishPitchMutation, useGetDocumentDownloadUrlMutation } from '../../../hooks/api'
import { usePdfScreenShare } from '../hooks/use-pdf-screen-share'
import { useSessionTimer } from '../hooks/use-session-timer'
import { finishAndLeave } from '../lib/finish-flow'
import { ParticipantTile } from './participant-tile'

// ─── Public API ──────────────────────────────────────────────────────────────

export interface PitchRoomProps {
  sessionId: string
  sessionToken: string
  pitchDeckId: string | null
  livekitUrl: string
  mediaSettings: MediaSettings
  onSessionEnd?: () => void
}

export function PitchRoom({
  sessionId,
  sessionToken,
  pitchDeckId,
  livekitUrl,
  mediaSettings,
  onSessionEnd,
}: PitchRoomProps) {
  const finishPitch = useFinishPitchMutation()
  const downloadUrlMutation = useGetDocumentDownloadUrlMutation()
  const [pitchDeckUrl, setPitchDeckUrl] = useState<string | null>(null)

  // biome-ignore lint/correctness/useExhaustiveDependencies: mutation.mutate is stable
  useEffect(() => {
    if (pitchDeckId) {
      downloadUrlMutation.mutate(
        { path: { id: pitchDeckId } },
        {
          onSuccess: (response) => {
            if (response.status === 'ok') {
              setPitchDeckUrl(response.data.downloadUrl)
            }
          },
        }
      )
    }
  }, [pitchDeckId])

  const handleDisconnect = () =>
    finishAndLeave(() => finishPitch.mutateAsync({ path: { pitchId: sessionId } }), onSessionEnd)

  const roomOptions = useMemo(
    () => ({
      audioCaptureDefaults: mediaSettings.audioDeviceId
        ? { deviceId: mediaSettings.audioDeviceId }
        : undefined,
      videoCaptureDefaults: mediaSettings.videoDeviceId
        ? { deviceId: mediaSettings.videoDeviceId }
        : undefined,
    }),
    [mediaSettings.audioDeviceId, mediaSettings.videoDeviceId]
  )

  if (!sessionToken || !livekitUrl || !pitchDeckUrl) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-background">
        <div className="rounded-xl border border-[#2a2a2a] bg-[#1f1f1f]/50 p-6 text-center">
          <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-[#f97316]/10">
            <Loader2 className="size-6 animate-spin text-[#f97316]" />
          </div>
          <h2 className="text-lg font-semibold text-white">Connecting</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Preparing your pitch deck and connecting
            <br />
            you with your AI investor.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 bg-background">
      <LiveKitRoom
        token={sessionToken}
        serverUrl={livekitUrl}
        connect={true}
        audio={mediaSettings.audioEnabled}
        video={mediaSettings.videoEnabled}
        options={roomOptions}
        className="h-screen"
        onDisconnected={handleDisconnect}
      >
        <ConferenceUI pitchDeckUrl={pitchDeckUrl} />
      </LiveKitRoom>
    </div>
  )
}

// ─── Internal: conference UI (must be inside LiveKitRoom provider) ───────────

function AgentConnectingCard() {
  return (
    <div className="flex h-full w-full items-center justify-center rounded-lg border-2 border-border bg-card">
      <div className="flex flex-col items-center text-center">
        <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-[#f97316]/10">
          <Loader2 className="size-6 animate-spin text-[#f97316]" />
        </div>
        <p className="text-sm font-medium text-white">AI Investor is connecting</p>
        <p className="mt-1 text-xs text-muted-foreground">This may take a moment...</p>
      </div>
    </div>
  )
}

function ConferenceUI({ pitchDeckUrl }: { pitchDeckUrl: string }) {
  const { localParticipant } = useLocalParticipant()
  const remoteParticipants = useRemoteParticipants()
  const { elapsedSeconds, resume: startTimer } = useSessionTimer(false)
  const { buttonProps } = useDisconnectButton({})

  const canvasRef = useRef<HTMLCanvasElement>(null)
  const { error } = usePdfScreenShare({ canvasRef, frameRate: 5 })

  if (error) {
    console.error('Screen share error:', error)
  }

  const remoteParticipant = remoteParticipants.find((p) => !p.identity.startsWith('agent-'))

  useEffect(() => {
    if (remoteParticipant) {
      startTimer()
    }
  }, [remoteParticipant, startTimer])

  return (
    <div className="flex h-full flex-col bg-background">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border bg-card/95 px-6 py-4 backdrop-blur-sm">
        <div className="flex items-center gap-4">
          <img src="/seedforge-logo.svg" alt="SeedForge" className="h-8" />
          {remoteParticipant && (
            <Badge variant="outline" size="lg" className="gap-2 py-2">
              <span className="relative flex size-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75" />
                <span className="relative inline-flex size-2 rounded-full bg-primary" />
              </span>
              <SessionTimer seconds={elapsedSeconds} />
            </Badge>
          )}
        </div>

        <ConfirmButton onConfirm={() => buttonProps.onClick?.()}>
          <LogOut className="size-4" />
          Leave
        </ConfirmButton>
      </div>

      {/* Main Content */}
      <div className="flex min-h-0 flex-1 overflow-hidden">
        {/* PDF Viewer (Left) */}
        {pitchDeckUrl && (
          <div className="h-full min-w-0 flex-1 border-r border-border">
            <PdfViewer url={pitchDeckUrl} canvasRef={canvasRef} streamingHeight={1080} />
          </div>
        )}

        {/* Cameras (Right) */}
        <div className="w-[min(40%,calc((100dvh-117px)*2/3+30px))] shrink-0 p-4">
          <div className="flex flex-col gap-4">
            <div className="aspect-4/3 w-full">
              {localParticipant && <ParticipantTile participant={localParticipant} />}
            </div>

            <div className="aspect-4/3 w-full">
              {remoteParticipant ? (
                <ParticipantTile participant={remoteParticipant} />
              ) : (
                <AgentConnectingCard />
              )}
            </div>
          </div>
        </div>
      </div>

      <RoomAudioRenderer />
    </div>
  )
}
